import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import type { ContactClientRoleEntity } from './contact-client-role.entity';
import type { ContactInteractionType } from './contact.entity';
import { ContactEntity } from './contact.entity';

/**
 * Why a person stopped being active at a customer. `erp_removed`: every record
 * behind the link was deleted by its source. `user_ended`: someone in Workd said
 * the person no longer works with the customer (kept even while ERP records
 * still list them, shown grayed). `record_moved`: the ERP reused the record for
 * a different person.
 */
export type ContactClientEndedReason = 'erp_removed' | 'user_ended' | 'record_moved';

/**
 * A person at one customer (PD-17064). Every link is backed by at least one
 * `client_contact` record on that customer; a link created in Workd creates a
 * Workd-owned record so SM1 shows the person there too. Links are never
 * deleted, only ended, so past visits and notes still show who it was.
 * Per-customer values (title, email, phone at this customer) come from that
 * customer's records; roles are owned by Workd.
 */
@Index('contact_client__contact_client', ['fkContactId', 'fkClientId'], {
	unique: true,
})
@Index('contact_client__fk_client', ['fkClientId'], {})
@Entity('contact_client')
export class ContactClientEntity extends BaseEntity {
	@Column('int', { name: 'fk_contact__id', unsigned: true })
	public fkContactId!: number;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('varchar', { name: 'title', length: 255, nullable: true })
	public title!: string | null;

	@Column('varchar', { name: 'email', length: 255, nullable: true })
	public email!: string | null;

	@Column('varchar', { name: 'phone', length: 64, nullable: true })
	public phone!: string | null;

	@Column('varchar', { name: 'extension', length: 64, nullable: true })
	public extension!: string | null;

	@Column({ type: 'boolean', name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@Column('datetime', { name: 'endedDate', nullable: true })
	public endedDate!: Date | null;

	@Column('varchar', { name: 'endedReason', length: 32, nullable: true })
	public endedReason!: ContactClientEndedReason | null;

	/** Live `client_contact` records behind this link. */
	@Column('int', { name: 'recordCount', unsigned: true, default: () => "'0'" })
	public recordCount!: number;

	@Column('datetime', { name: 'lastContactedDate', nullable: true })
	public lastContactedDate!: Date | null;

	@Column('varchar', { name: 'lastContactedType', length: 32, nullable: true })
	public lastContactedType!: ContactInteractionType | null;

	@ManyToOne(() => ContactEntity, (contact) => contact.contactClients, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_contact__id', referencedColumnName: 'id' }])
	public contact!: ContactEntity;

	@ManyToOne(() => ClientEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity;

	@OneToMany('ContactClientRoleEntity', 'contactClient')
	public roles!: Array<ContactClientRoleEntity>;
}
